<template>
    <div class="calculator calculator-investment">
        <div class="container p-0">
            <p class="text-start">{{ microcopy?.summary }}</p>
            <div class="row flex-row calculator__row">
                <div class="col-lg-6 calculator__form ">
                    <form>
                        <p class="text-start">{{ microcopy?.introText }}</p>
                        <TextInputField
                            allow-zero
                            :minimum-value="microcopy?.lumpSumMinimum"
                            :maximum-value="microcopy?.lumpSumMaximum"
                            input-type="currency"
                            :label-text="microcopy?.lumpSumLabel"
                            :error-text="microcopy?.lumpSumValidationMessage"
                            @field-input="(payload) => {
                                isValid.lumpSum = payload.isValid;
                                lumpSum = payload.value;
                                hasError.lumpSum = payload.hasError
                            }"
                        >
                            <template v-slot:tooltip>
                                <Tooltip :content="microcopy?.lumpSumToolTip" />
                            </template>
                        </TextInputField>
                        <TextInputField
                            allow-zero
                            :minimum-value="microcopy?.regularAmountMinimum"
                            :maximum-value="microcopy?.regularAmountMaximum"
                            input-type="currency"
                            :label-text="microcopy?.regularAmountLabel"
                            :error-text="microcopy?.regularAmountValidationMessage"
                            @field-input="(payload) => {
                                regularAmount = payload.value;
                                isValid.regularAmount = payload.isValid;
                                hasError.regularAmount = payload.hasError
                            }"
                        >
                            <template v-slot:tooltip>
                                <Tooltip :content="microcopy?.regularAmountTooltip" />
                            </template>
                        </TextInputField>

                        <hr />
                        <div class="input-wrapper">
                            <div class="row justify-content-between flex-nowrap has-tooltip">
                                <label :id="cleanStrForElemId(this.microcopy?.investmentTermLabel + '-label')" class="col-auto">{{ microcopy?.investmentTermLabel }}</label>
                                <Tooltip :content="microcopy?.investmentTermTooltip" />
                            </div>
                            <DropdownField
                                :options="microcopy?.investmentTermYears"
                                :prefillSelect="microcopy?.investmentTermDefault"
                                :aria-labelledby="`${cleanStrForElemId(microcopy?.investmentTermLabel)}-label`"
                                @selected="(payload) => {
                                    selectedInvestmentPeriod = payload;
                                }"
                            />
                        </div>
                        <div class="growth has-tooltip">
                            <fieldset class="d-flex flex-column flex-wrap">
                                <div class="row flex-nowrap has-tooltip tooltip-inline">
                                    <legend class="col-auto">{{ microcopy?.growthRateLabel }}</legend>
                                    <Tooltip :content="microcopy?.growthRateTooltip" />
                                </div>
                                <div class="radio-container__wrapper">
                                    <label
                                        class="col radio-container radio-container--growth align-items-center justify-content-center d-flex text-center"
                                        :class="{ active: growthRate == microcopy?.growthRateLowValue }"
                                    >

                                        {{ microcopy?.growthRateLowLabel }} {{ microcopy?.growthRateLowValue }}%
                                        <input
                                            type="radio"
                                            v-model.number="growthRate"
                                            :name="`Growth - ${microcopy?.growthRateLowValue}%`"
                                            :value="microcopy?.growthRateLowValue"
                                        />
                                        <span class="radiobtn"></span>
                                    </label>
                                    <label
                                        class="col radio-container radio-container--growth align-items-center justify-content-center d-flex text-center"
                                        :class="{ active: growthRate == microcopy?.growthRateMediumValue }"
                                    >
                                        {{ microcopy?.growthRateMediumLabel }} {{ microcopy?.growthRateMediumValue }}%
                                        <input
                                            type="radio"
                                            v-model.number="growthRate"
                                            :name="`Growth - ${microcopy?.growthRateMediumValue}%`"
                                            :value="microcopy?.growthRateMediumValue"
                                        />
                                        <span class="radiobtn"></span>
                                    </label>
                                    <label
                                        class="col radio-container radio-container--growth align-items-center justify-content-center d-flex text-center"
                                        :class="{ active: growthRate == microcopy?.growthRateHighValue }"
                                    >
                                        {{ microcopy?.growthRateHighLabel }} {{ microcopy?.growthRateHighValue }}%
                                        <input
                                            type="radio"
                                            v-model.number="growthRate"
                                            :name="'Growth - ${microcopy?.growthRateHighValue}%'"
                                            :value="microcopy?.growthRateHighValue"
                                        />
                                        <span class="radiobtn"></span>
                                    </label>
                                </div>
                            </fieldset>

                        </div>
                    </form>
                    <button
                        class="btn btn-primary btn-calculate"
                        :disabled="!allInputsValid"
                        @click.prevent="recalculateSummaryData()"
                    >
                        {{ calculateButtonText }}
                    </button>
                </div>
                <div class="col-lg-6 results-container d-none d-lg-block p-0">
                    <div class="row">
                        <div class="col">
                            <div class="results">
                                <h3 class="text-start">{{ microcopy?.resultPanelHeading }}</h3>
                                <hr class="hr hr--small" />
                                <div class="row">
                                    <div class="col text-start">
                                        <p>
                                            <span class="results-label">{{ microcopy?.potentialReturnsLabel }}</span><br />
                                            <span class="results-amount">{{ formatPrice(summaryData.potentialReturn) }}</span>
                                        </p>
                                    </div>
                                </div>
                                <hr />
                                <div class="row">
                                    <div class="col-6 text-start">
                                        <p>
                                            <span class="results-label">{{ microcopy?.contributionLabel }}</span><br />
                                            <span class="results-amount">{{ formatPrice(summaryData.yourContribution) }}</span>
                                        </p>
                                    </div>
                                    <div class="col-auto text-start">
                                        <p>
                                            <span class="results-label">{{ microcopy?.investmentGainLabel }}</span><br />
                                            <span class="results-amount"> {{ formatPrice(summaryData.investmentGain) }}</span>
                                        </p>
                                    </div>
                                </div>
                                <hr class="hr hr--small" />
                                <div class="row">
                                    <div class="col text-start">
                                        <div v-html="microcopy?.complianceMessage"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="results-container d-block d-lg-none mt-5">
            <div class="container">
                <div class="col results">
                    <div class="row">
                        <div class="col-12 text-center">
                            <p>
                                <span class="results-label">{{ microcopy?.potentialReturnsLabel }}</span><br />
                                <span class="results-amount">{{ formatPrice(summaryData.potentialReturn) }}</span>
                            </p>
                        </div>
                    </div>
                    <hr class="hr--small" />
                    <div class="row flex-row flex-nowrap">
                        <div class="col-6 text-center">
                            <p>
                                <span class="results-label">{{ microcopy?.contributionLabel }}</span><br />
                                <span class="results-amount">{{ formatPrice(summaryData.yourContribution) }}</span>
                            </p>
                        </div>
                        <span class="vr"></span>
                        <div class="col-6 text-center">
                            <p>
                                <span class="results-label">{{ microcopy?.investmentGainLabel }}</span><br />
                                <span class="results-amount"> {{ formatPrice(summaryData.investmentGain) }}</span>
                            </p>
                        </div>
                    </div>
                    <hr class="hr--small" />
                    <div class="row">
                        <div class="col-12 text-start">
                            <div v-html="microcopy?.complianceMessage"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import helper from '../mixins/helpers.js';
import { cleanStrForElemId } from '../../lib/utils/helpers';
import { formatPrice } from '../utils';
import DropdownField from "../shared/components/DropdownField.vue";
import TextInputField from "../shared/components/TextInputField.vue";
import Tooltip from '../shared/components/Tooltip.vue';

export default {
    components: {
        TextInputField,
        DropdownField,
        Tooltip
    },
    mixins: [
        helper
    ],
    data() {
        return {
            lumpSum: '',
            regularAmount: '',
            growthRate: 5,
            managementFee: 1.23,
            inputError: '',
            selectedInvestmentPeriod: '',
            selectedInvestmentGrowthRate: '',
            toolTips: [
                {
                    id: "term",
                    isShown: false,
                    content: ""
                },
                {
                    id: "growthRate",
                    isShown: false,
                    content: ""
                }
            ],
            summaryData: {
                potentialReturn: 0,
                yourContribution: 0,
                investmentGain: 0
            },
            calculateButtonClicked: false,
            isValid: {
                lumpSum: false,
                regularAmount: false,
            },
            hasError: {
                lumpSum: false,
                regularAmount: false,
            },
        };
    },
    computed: {
        microcopy() {
            return window?.potentialIsaGrowthCalc;
        },
        term() {
            return parseInt(this.selectedInvestmentPeriod);
        },
        decimalisedRate() {
            return this.isNumberChecker(this.growthRate) / 100;
        },
        decimalisedRateMonthly() {
            return (1 + this.decimalisedRate) ** (1 / 12);
        },
        monthlyGrowRate() {
            const feePercent = this.managementFee / 100;
            return (this.decimalisedRateMonthly  * (1 - feePercent / 12)) - 1;
        },

        potentialReturn() {
            const numberMonths = this.isNumberChecker(this.term) * 12;
            const lumpSumReturn = this.isNumberChecker(this.lumpSum) * (1 + this.monthlyGrowRate) ** this.isNumberChecker(numberMonths);
            const regularReturn = this.isNumberChecker(this.regularAmount) * ((( 1 + this.monthlyGrowRate) ** numberMonths - 1) / this. monthlyGrowRate) * (1 + this. monthlyGrowRate);
			
            this.scrollToVisibleResults();

//             console.log(`
// ISA Growth Debug Output
// =======================
// decimalisedRate: ${this.decimalisedRate}
// decimalisedRateMonthly: ${this.decimalisedRateMonthly}
// numberMonths: ${numberMonths}
// managementFee: ${this.managementFee}
// monthlyGrowRate: ${this.monthlyGrowRate}
// lumpSumReturn: ${lumpSumReturn}
// regularReturn: ${regularReturn}
// total: ${lumpSumReturn + regularReturn}
// `);
            return lumpSumReturn + regularReturn;
        },

        yourContribution() {
            const numberMonths = this.isNumberChecker(this.term) * 12;
            const totalRegular = this.isNumberChecker(this.regularAmount) * numberMonths;
            return this.isNumberChecker(this.lumpSum) + totalRegular;
        },

        investmentGain() {
            return this.isNumberChecker(this.potentialReturn - this.yourContribution)
        },

        calculateButtonText() {
            return this.calculateButtonClicked ? this.microcopy?.recalculateButtonLabel : this.microcopy?.calculateButtonLabel;
        },
        allInputsValid() {
            return (this.isValid.lumpSum || this.isValid.regularAmount) && this.hasError.lumpSum == false && this.hasError.regularAmount == false  && this.term !== '';
        }
    },
    mounted() {
        // Tooltips
        this.toolTips.find(tooltip => tooltip.id == "term").content = this.microcopy?.investmentTermTooltip;
        this.toolTips.find(tooltip => tooltip.id == "growthRate").content = this.microcopy?.growthRateTooltip;

    },
    methods: {
        toggleToolTip(index) {
            this.toolTips[index].isShown = !this.toolTips[index].isShown;
        },
        closePreviousToolTips(index) {
            this.toolTips.forEach((tooltip) => {
                tooltip.isShown = false
            })

            this.toolTips[index].isShown = true

        },
        recalculateSummaryData() {
            this.summaryData = {
                potentialReturn: this.potentialReturn,
                yourContribution: this.yourContribution,
                investmentGain: this.investmentGain
            };
            this.calculateButtonClicked = true;
        },
        formatPrice,
		cleanStrForElemId,
    }
}
</script>
<style scoped>
.calculator #tooltip {
    background-color: #fff8e7;
    border: 1px solid #fed770;
    border-radius: 4px;
    margin-left: 10px;
    margin-top: 0;
    padding: 24px;
    position: absolute;
    left: 0;
    text-align: left;
    top: 0;
    width: 358px;
    z-index: 2;
}
</style>
